import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconChevronDown } from '@tabler/icons-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import type { ModuleNavNode } from '@/hooks/useModuleConfig';

export interface MobileNavSheetProps {
  open: boolean;
  onClose: () => void;
  moduleTree: ModuleNavNode[];
  /** uid of the module matching the current route, expanded by default. */
  currentModuleKey?: string | null;
}

/**
 * Mobile navigation drawer — a standard hierarchical nav.
 *
 * Modules are the top level; each module's pages are nested beneath it as
 * children. An earlier version hoisted the *current* module's pages into a
 * separate "Pages" group above the module list, which mirrored the desktop
 * sub-nav bar but read as two unrelated menus and hid the fact that those pages
 * belong to the module directly below them.
 *
 * The module on the current route starts expanded; the rest collapse so the
 * whole product stays scannable in one screen.
 */
export function MobileNavSheet({
  open,
  onClose,
  moduleTree,
  currentModuleKey,
}: MobileNavSheetProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedKey, setExpandedKey] = useState<string | null>(currentModuleKey ?? null);

  // Re-sync when the drawer reopens on a different route, so it always opens
  // showing where you actually are.
  useEffect(() => {
    if (open) setExpandedKey(currentModuleKey ?? null);
  }, [open, currentModuleKey]);

  const go = (href: string) => {
    onClose();
    navigate(href);
  };

  const isChildActive = (href: string) =>
    location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <SheetContent
        side="left"
        className="w-[86vw] max-w-[340px] p-0 flex flex-col bg-[var(--background-page-content)] border-[var(--border-default)]"
        aria-label="Navigation"
      >
        <SheetHeader className="px-5 pt-5 pb-4 border-b border-[var(--border-default)]">
          <SheetTitle className="text-body-m font-semibold text-[var(--text-content-title)]">
            Menu
          </SheetTitle>
        </SheetHeader>

        {/* No search here — the drawer is for navigating to a module, and the
            search bar pushed the module list down a full row to answer a
            different question. Desktop keeps its search in the top bar. */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <nav className="py-2" aria-label="Modules">
            {moduleTree.map((module) => {
              const isCurrent = module.key === currentModuleKey;
              const isExpanded = expandedKey === module.key;
              const hasChildren = module.children.length > 0;

              return (
                <div key={module.key}>
                  <div
                    className={`flex items-stretch ${
                      isCurrent ? 'bg-[var(--background-card-elevated)]' : ''
                    }`}
                  >
                    {/* Tapping the label navigates to the module landing page */}
                    <button
                      type="button"
                      onClick={() => go(module.href)}
                      className={`flex-1 flex items-center gap-3 pl-5 pr-2 py-3 text-left text-[15px] min-w-0 ${
                        isCurrent
                          ? 'text-[var(--color-forest)] font-semibold'
                          : 'text-[var(--text-content-title)]'
                      }`}
                    >
                      <span
                        className={`flex items-center flex-shrink-0 ${
                          isCurrent
                            ? 'text-[var(--color-forest)]'
                            : 'text-[var(--text-content-sub-title)]'
                        }`}
                      >
                        {module.icon}
                      </span>
                      <span className="truncate">{module.name}</span>
                    </button>

                    {/* Chevron expands children without leaving the page */}
                    {hasChildren && (
                      <button
                        type="button"
                        onClick={() => setExpandedKey(isExpanded ? null : module.key)}
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${module.name}`}
                        className="px-4 flex items-center flex-shrink-0 text-[var(--text-content-sub-title)]"
                      >
                        <IconChevronDown
                          size={16}
                          className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </button>
                    )}
                  </div>

                  {/* Child pages */}
                  {hasChildren && isExpanded && (
                    <div className="pb-1">
                      {module.children.map((child) => {
                        const active = isChildActive(child.href);
                        return (
                          <button
                            key={child.href}
                            type="button"
                            onClick={() => go(child.href)}
                            className={`w-full flex items-center gap-2 py-2.5 pr-5 text-left text-[14px] border-l-[3px] ml-5 ${
                              active
                                ? 'border-[var(--color-forest)] text-[var(--color-forest)] font-semibold pl-4'
                                : 'border-[var(--border-default)] text-[var(--text-content-sub-title)] pl-4'
                            }`}
                          >
                            <span className="flex-1 truncate">{child.label}</span>
                            {child.badge !== undefined && child.badge > 0 && (
                              <span className="text-[11px] bg-[var(--color-gold-subtle)] text-[var(--color-text-gold)] border border-[var(--color-gold-border)] px-1.5 rounded-full flex-shrink-0">
                                {child.badge}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </SheetContent>
    </Sheet>
  );
}
